import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Bell, UserPlus, UserMinus, MapPin, Cake, VenetianMask,
  MessageCircle, Check, X, Loader2, BadgeCheck, Lock, Clock, SlidersHorizontal,
  Ban, ShieldOff, Rss, Heart, BadgeCheck as Verified,
} from "lucide-react";
import { socialApi, resolveMedia } from "@/api/social";
import { useSession } from "@/context/SessionContext";
import DefaultAvatar from "@/components/social/DefaultAvatar";

const GENDERS = ["Male", "Female", "Non-Binary", "Prefer Not to Say"];

export const STATUS_PILL = {
  FRIEND: { label: "Friend", cls: "bg-emerald-500 text-white border-charcoal" },
  PENDING_INBOUND: { label: "Wants to connect", cls: "bg-amber-400 text-charcoal border-charcoal" },
  PENDING_OUTBOUND: { label: "Requested", cls: "bg-charcoal text-gold border-gold" },
  NOT_FRIENDS: { label: "Discover", cls: "bg-white text-charcoal/60 border-charcoal/40" },
  BLOCKED: { label: "Blocked", cls: "bg-red-600 text-white border-charcoal" },
};

export function Avatar({ src, name, online, size = 46, token, gender }) {
  const resolved = resolveMedia(src, token);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <div className="rounded-lg border-2 border-charcoal overflow-hidden grid place-items-center shadow-[2px_2px_0px_#1A1A1A] w-full h-full">
        {resolved ? (
          <img src={resolved} alt={name} className="w-full h-full object-cover" />
        ) : (
          <DefaultAvatar name={name} gender={gender} />
        )}
      </div>
      {online != null && (
        <span
          className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-cream ${online ? "bg-emerald-500" : "bg-charcoal/30"}`}
        />
      )}
    </div>
  );
}

export function UserTile({ person, onClick, token }) {
  const pill = STATUS_PILL[person.status] || STATUS_PILL.NOT_FRIENDS;
  return (
    <button
      onClick={() => onClick(person)}
      data-testid={`user-tile-${person.id}`}
      className="w-full flex items-center gap-3 bg-white border-2 border-charcoal rounded-2xl px-3 py-2.5 shadow-[3px_3px_0px_#1A1A1A] transition-[transform,box-shadow] duration-150 hover:-translate-y-[1px] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none text-left"
    >
      <Avatar src={person.avatar} name={person.name} online={person.online} token={token} gender={person.gender} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="font-serif text-base text-charcoal leading-none truncate">{person.name}</p>
          {person.verified && <BadgeCheck size={14} className="text-emerald-500 shrink-0" />}
        </div>
        <p className="font-mono text-[10px] text-charcoal/45 truncate">@{person.handle}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {person.online
            ? <span data-testid={`tile-online-${person.id}`} className="font-mono text-[8px] font-black uppercase tracking-wider text-emerald-600">● Online</span>
            : <span className="font-mono text-[8px] uppercase tracking-wider text-charcoal/35">Offline</span>}
          {person.location && <span className="font-mono text-[8px] text-charcoal/40 truncate inline-flex items-center gap-0.5"><MapPin size={9} /> {person.location}</span>}
        </div>
      </div>
      <span className={`shrink-0 font-mono text-[8px] font-black uppercase tracking-wider border-2 rounded-full px-2 py-1 ${pill.cls}`}>
        {pill.label}
      </span>
    </button>
  );
}

export function ProfileCard({ person, onClose, onMutate, busy, token, onMessage }) {
  const msgSoon = () => toast("Direct messaging — coming soon", { icon: "🔒" });
  const btn = "w-full py-2.5 border-2 border-charcoal font-mono text-[11px] font-black uppercase tracking-wider rounded-lg shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none transition-[transform,box-shadow] duration-150 disabled:opacity-60 disabled:active:translate-x-0 disabled:active:translate-y-0";
  const followBtn = (
    <button onClick={() => onMutate("follow")} disabled={busy} data-testid="popup-follow-btn" className={`${btn} ${person.following ? "bg-white text-charcoal/70" : "bg-charcoal text-gold"}`}>
      <span className="inline-flex items-center gap-1.5"><Rss size={13} /> {person.following ? "Following" : "Follow"}</span>
    </button>
  );
  const blockBtn = (
    <button onClick={() => onMutate("block")} disabled={busy} data-testid="popup-block-btn" className={`${btn} bg-red-50 text-red-600`}>
      <span className="inline-flex items-center gap-1.5"><Ban size={13} /> Block</span>
    </button>
  );
  return (
    <div className="absolute inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="social-action-popup">
      <div className="w-[338px] max-w-[calc(100vw-32px)] bg-cream border-4 border-charcoal p-4 rounded-2xl shadow-[5px_5px_0px_#1A1A1A] space-y-3 max-h-[90dvh] overflow-y-auto no-scrollbar">
        {/* Identity header */}
        <div className="flex items-center gap-3 pb-3 border-b-2 border-charcoal/10">
          <Avatar src={person.avatar} name={person.name} online={person.online} size={56} token={token} gender={person.gender} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1">
              <h4 className="font-serif text-xl text-charcoal leading-none truncate">{person.name}</h4>
              {person.verified && <Verified size={16} className="text-emerald-500 shrink-0" />}
            </div>
            <span className="font-mono text-[10px] text-charcoal/40">@{person.handle}</span>
            <div className="mt-1">
              {person.online
                ? <span className="font-mono text-[9px] font-black uppercase tracking-wider text-emerald-600">● Online now</span>
                : <span className="font-mono text-[9px] uppercase tracking-wider text-charcoal/40">Offline</span>}
            </div>
          </div>
          <button onClick={onClose} data-testid="popup-close-btn" className="w-7 h-7 grid place-items-center rounded-lg border-2 border-charcoal bg-white shadow-[1.5px_1.5px_0px_#1A1A1A] self-start">
            <X size={14} strokeWidth={2.6} className="text-charcoal" />
          </button>
        </div>

        {/* Basic info */}
        <div className="flex flex-wrap gap-1.5" data-testid="popup-basic-info">
          {person.location && <span className="inline-flex items-center gap-1 rounded-full border-2 border-charcoal bg-white px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-charcoal shadow-[1.5px_1.5px_0px_#1A1A1A]"><MapPin size={11} className="text-gold" />{person.location}</span>}
          {person.age != null && <span className="inline-flex items-center gap-1 rounded-full border-2 border-charcoal bg-white px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-charcoal shadow-[1.5px_1.5px_0px_#1A1A1A]"><Cake size={11} className="text-gold" />{person.age} yrs</span>}
          {person.gender && <span className="inline-flex items-center gap-1 rounded-full border-2 border-charcoal bg-white px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-charcoal shadow-[1.5px_1.5px_0px_#1A1A1A]"><VenetianMask size={11} className="text-gold" />{person.gender}</span>}
          {person.relationship_status && <span className="inline-flex items-center gap-1 rounded-full border-2 border-charcoal bg-white px-2.5 py-1 font-mono text-[9px] uppercase tracking-wider text-charcoal shadow-[1.5px_1.5px_0px_#1A1A1A]"><Heart size={11} className="text-gold" />{person.relationship_status}</span>}
        </div>
        {person.interests?.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="popup-interests">
            {person.interests.slice(0, 8).map((t) => (
              <span key={t} className="rounded-full border-2 border-charcoal bg-gold/20 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-charcoal">{t}</span>
            ))}
          </div>
        )}
        {person.bio && <p className="font-mono text-[11px] text-charcoal font-medium leading-relaxed bg-white border-2 border-charcoal/25 rounded-xl p-2.5" data-testid="popup-bio">{person.bio}</p>}

        {/* Actions */}
        <div className="space-y-2">
          {person.status === "BLOCKED" ? (
            <button onClick={() => onMutate("unblock")} disabled={busy} data-testid="popup-unblock-btn" className={`${btn} bg-gold text-black`}>
              {busy ? "PROCESSING…" : <span className="inline-flex items-center gap-1.5"><ShieldOff size={13} /> Unblock</span>}
            </button>
          ) : person.status === "FRIEND" ? (
            <>
              <button onClick={() => (onMessage ? onMessage(person) : msgSoon())} disabled={busy} data-testid="popup-message-btn" className={`${btn} bg-white`}>
                <span className="inline-flex items-center gap-1.5"><MessageCircle size={13} /> Send Message</span>
              </button>
              {followBtn}
              <button onClick={() => onMutate("unfriend")} disabled={busy} data-testid="popup-unfriend-btn" className={`${btn} bg-red-50 text-red-600`}>
                {busy ? "PROCESSING…" : <span className="inline-flex items-center gap-1.5"><UserMinus size={13} /> Unfriend</span>}
              </button>
              {blockBtn}
            </>
          ) : person.status === "PENDING_INBOUND" ? (
            <>
              <div className="text-center font-mono text-[8px] font-black uppercase text-amber-600 bg-amber-50 py-1.5 border-2 border-dashed border-amber-300 rounded-lg flex items-center justify-center gap-1">
                <Clock size={11} /> Incoming Request
              </div>
              <button onClick={() => onMutate("accept")} disabled={busy} data-testid="popup-accept-btn" className={`${btn} bg-emerald-500 text-white`}>
                {busy ? "SYNCING…" : <span className="inline-flex items-center gap-1.5"><Check size={13} /> Accept</span>}
              </button>
              <button onClick={() => onMutate("decline")} disabled={busy} data-testid="popup-decline-btn" className={`${btn} bg-white text-charcoal/70`}>
                <span className="inline-flex items-center gap-1.5"><X size={13} /> Decline</span>
              </button>
              {blockBtn}
            </>
          ) : person.status === "PENDING_OUTBOUND" ? (
            <>
              <div className="text-center font-mono text-[9px] font-black uppercase text-charcoal/60 bg-white py-2 border-2 border-charcoal rounded-lg" data-testid="popup-requested-state">
                Request Sent · Awaiting Reply
              </div>
              {followBtn}
              {blockBtn}
            </>
          ) : (
            <>
              <button onClick={() => onMutate("request")} disabled={busy} data-testid="popup-add-friend-btn" className={`${btn} bg-gold text-black`}>
                {busy ? "TRANSMITTING…" : <span className="inline-flex items-center gap-1.5"><UserPlus size={13} /> Add Friend</span>}
              </button>
              {followBtn}
              {blockBtn}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationsFolder({ items, onBack }) {
  return (
    <div className="h-full flex flex-col bg-cream" data-testid="notifications-folder">
      <header className="shrink-0 px-4 pt-3 pb-2 flex items-center gap-3 border-b-2 border-charcoal/10">
        <button onClick={onBack} data-testid="notifications-back-btn" className="w-9 h-9 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={16} strokeWidth={2.4} className="text-charcoal" />
        </button>
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Activity</p>
          <h1 className="font-serif text-2xl text-charcoal leading-none">Notifications</h1>
        </div>
      </header>
      <div className="flex-1 overflow-y-auto no-scrollbar px-4 py-3 space-y-2">
        {items.length === 0 && (
          <p className="text-center font-mono text-xs text-charcoal/40 mt-10" data-testid="notifications-empty">No notifications yet.</p>
        )}
        {items.map((n) => (
          <div key={n.id} data-testid={`notification-${n.id}`} className={`flex items-center gap-3 border-2 border-charcoal rounded-xl px-3 py-2.5 ${n.unread ? "bg-gold/20" : "bg-white"}`}>
            <span className="w-8 h-8 rounded-lg border-2 border-charcoal bg-charcoal text-gold grid place-items-center shrink-0">
              {n.type === "FRIEND_ACCEPT" ? <Check size={15} /> : <UserPlus size={15} />}
            </span>
            <p className="font-mono text-[11px] text-charcoal leading-snug">
              <span className="font-black">{n.actor_name}</span>{" "}
              {n.type === "FRIEND_ACCEPT" ? "accepted your friend request." : "sent you a friend request."}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

const EMPTY_FILTERS = { q: "", location: "", gender: "", age_min: "", age_max: "" };

export default function SocialDirectory({ onBack, onUnreadChange, onMessage }) {
  const { token } = useSession();
  const [tab, setTab] = useState("FRIENDS");
  const [view, setView] = useState("LIST"); // LIST | NOTIFICATIONS
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [people, setPeople] = useState([]);
  const [friends, setFriends] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const debounceRef = useRef(null);

  const unread = notifs.filter((n) => n.unread).length;

  const cleanFilters = useCallback(() => {
    const f = {};
    if (filters.q.trim()) f.q = filters.q.trim();
    if (filters.location.trim()) f.location = filters.location.trim();
    if (filters.gender) f.gender = filters.gender;
    if (filters.age_min !== "") f.age_min = Number(filters.age_min);
    if (filters.age_max !== "") f.age_max = Number(filters.age_max);
    return f;
  }, [filters]);

  const loadDirectory = useCallback(async () => {
    try {
      const dir = await socialApi.directory(cleanFilters());
      setPeople(dir.people);
    } catch { toast.error("Could not load directory"); }
  }, [cleanFilters]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [fr, bl, nt] = await Promise.all([
        socialApi.friends(), socialApi.blocked(), socialApi.notifications(),
      ]);
      setFriends(fr.friends);
      setBlocked(bl.blocked);
      setNotifs(nt.notifications);
      onUnreadChange?.(nt.unread);
      await loadDirectory();
    } catch { toast.error("Could not load social hub"); }
    finally { setLoading(false); }
  }, [loadDirectory, onUnreadChange]);

  useEffect(() => { loadAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced live search when in the Discover tab.
  useEffect(() => {
    if (tab !== "DISCOVER") return;
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { loadDirectory(); }, 350);
    return () => clearTimeout(debounceRef.current);
  }, [filters, tab, loadDirectory]);

  const openNotifications = async () => {
    setView("NOTIFICATIONS");
    if (unread > 0) {
      await socialApi.markRead();
      setNotifs((prev) => prev.map((n) => ({ ...n, unread: false })));
      onUnreadChange?.(0);
    }
  };

  const mutate = async (action) => {
    if (!selected) return;
    setBusy(true);
    try {
      if (action === "request") await socialApi.request(selected.id);
      else if (action === "unfriend") await socialApi.unfriend(selected.id);
      else if (action === "block") await socialApi.blockUser(selected.id);
      else if (action === "unblock") await socialApi.unblock(selected.id);
      else if (action === "follow") {
        const r = await socialApi.follow(selected.id);
        toast.success(r.active ? "Following" : "Unfollowed");
        setSelected((s) => ({ ...s, following: r.active }));
        setBusy(false);
        loadDirectory();
        return; // stay open so the user can chain actions
      } else await socialApi.respond(selected.id, action); // accept | decline
      toast.success(
        action === "accept" ? "Friend added" :
        action === "request" ? "Request sent" :
        action === "decline" ? "Request declined" :
        action === "block" ? "User blocked" :
        action === "unblock" ? "User unblocked" : "Removed"
      );
      setSelected(null);
      await loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  if (view === "NOTIFICATIONS") {
    return <NotificationsFolder items={notifs} onBack={() => setView("LIST")} />;
  }

  const list = tab === "FRIENDS" ? friends : tab === "BLOCKED" ? blocked : people;
  const activeFilterCount = Object.keys(cleanFilters()).filter((k) => k !== "q").length;

  return (
    <div className="h-full flex flex-col bg-cream relative" data-testid="social-directory">
      {selected && (
        <ProfileCard person={selected} busy={busy} token={token} onClose={() => setSelected(null)} onMutate={mutate} onMessage={onMessage} />
      )}

      {/* Header */}
      <header className="shrink-0 px-4 pt-3 pb-2 flex items-center gap-3">
        <button onClick={onBack} data-testid="social-back-btn" className="w-9 h-9 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={16} strokeWidth={2.4} className="text-charcoal" />
        </button>
        <div className="leading-none flex-1">
          <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-gold">Community</p>
          <h1 className="font-serif text-2xl text-charcoal">Friends</h1>
        </div>
        <button onClick={openNotifications} data-testid="social-notifications-btn" aria-label="Notifications" className="relative w-9 h-9 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <Bell size={16} strokeWidth={2.4} className="text-charcoal" />
          {unread > 0 && (
            <span data-testid="social-unread-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white border-2 border-charcoal grid place-items-center font-mono text-[9px] font-black">
              {unread}
            </span>
          )}
        </button>
      </header>

      {/* Tabs */}
      <div className="shrink-0 px-4 flex gap-2 mb-2" data-testid="social-tabs">
        {[
          { k: "FRIENDS", label: `Friends · ${friends.length}` },
          { k: "DISCOVER", label: "Discover" },
          { k: "BLOCKED", label: `Blocked · ${blocked.length}` },
        ].map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`social-tab-${t.k.toLowerCase()}`}
            className={[
              "flex-1 font-mono text-[10px] font-black uppercase tracking-widest py-2 rounded-lg border-2 border-charcoal transition-colors duration-150",
              tab === t.k ? "bg-charcoal text-gold" : "bg-white text-charcoal/60",
            ].join(" ")}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search + filters (Discover only) */}
      {tab === "DISCOVER" && (
        <div className="shrink-0 px-4 mb-2 space-y-2">
          <div className="flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 bg-white border-2 border-charcoal rounded-xl px-3 py-2 shadow-[2px_2px_0px_#1A1A1A]">
              <Search size={15} className="text-charcoal/50" />
              <input
                value={filters.q}
                onChange={(e) => setFilters((s) => ({ ...s, q: e.target.value }))}
                placeholder="Search by username…"
                data-testid="social-search-input"
                className="flex-1 bg-transparent outline-none font-mono text-xs text-charcoal placeholder:text-charcoal/35"
              />
            </div>
            <button
              onClick={() => setShowFilters((v) => !v)}
              data-testid="filter-toggle-btn"
              className={`relative w-10 h-[42px] grid place-items-center rounded-xl border-2 border-charcoal shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${showFilters ? "bg-charcoal text-gold" : "bg-white text-charcoal"}`}
            >
              <SlidersHorizontal size={16} />
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-gold text-black border-2 border-charcoal grid place-items-center font-mono text-[8px] font-black">{activeFilterCount}</span>
              )}
            </button>
          </div>

          {showFilters && (
            <div className="bg-white border-2 border-charcoal rounded-xl p-3 shadow-[2px_2px_0px_#1A1A1A] space-y-2.5" data-testid="filter-panel">
              <label className="block">
                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><MapPin size={10} className="text-gold" />Location</span>
                <input value={filters.location} onChange={(e) => setFilters((s) => ({ ...s, location: e.target.value }))} data-testid="filter-location" placeholder="e.g. Adelaide" className="mt-1 w-full bg-cream border-2 border-charcoal rounded-lg px-2.5 py-1.5 font-mono text-[11px] text-charcoal outline-none focus:ring-2 focus:ring-gold" />
              </label>
              <label className="block">
                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><VenetianMask size={10} className="text-gold" />Gender</span>
                <select value={filters.gender} onChange={(e) => setFilters((s) => ({ ...s, gender: e.target.value }))} data-testid="filter-gender" className="mt-1 w-full bg-cream border-2 border-charcoal rounded-lg px-2.5 py-1.5 font-mono text-[11px] text-charcoal outline-none focus:ring-2 focus:ring-gold">
                  <option value="">Any</option>
                  {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
              </label>
              <div>
                <span className="font-mono text-[8px] uppercase tracking-[0.2em] text-charcoal/50 flex items-center gap-1"><Cake size={10} className="text-gold" />Age range</span>
                <div className="mt-1 flex items-center gap-2">
                  <input type="number" min="0" value={filters.age_min} onChange={(e) => setFilters((s) => ({ ...s, age_min: e.target.value }))} data-testid="filter-age-min" placeholder="Min" className="w-full bg-cream border-2 border-charcoal rounded-lg px-2.5 py-1.5 font-mono text-[11px] text-charcoal outline-none focus:ring-2 focus:ring-gold" />
                  <span className="font-mono text-charcoal/40 text-xs">—</span>
                  <input type="number" min="0" value={filters.age_max} onChange={(e) => setFilters((s) => ({ ...s, age_max: e.target.value }))} data-testid="filter-age-max" placeholder="Max" className="w-full bg-cream border-2 border-charcoal rounded-lg px-2.5 py-1.5 font-mono text-[11px] text-charcoal outline-none focus:ring-2 focus:ring-gold" />
                </div>
              </div>
              <button onClick={() => setFilters(EMPTY_FILTERS)} data-testid="filter-clear-btn" className="w-full bg-cream border-2 border-charcoal font-mono text-[9px] font-black uppercase tracking-widest py-2 rounded-lg text-charcoal/70 active:translate-x-[1px] active:translate-y-[1px]">Clear filters</button>
            </div>
          )}
        </div>
      )}

      {/* List — vertical scroll (music-vault style) */}
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-2">
        {loading ? (
          <div className="grid place-items-center py-16"><Loader2 size={26} className="text-gold animate-spin" /></div>
        ) : list.length === 0 ? (
          <p className="text-center font-mono text-xs text-charcoal/40 mt-10" data-testid="social-empty">
            {tab === "FRIENDS" ? "No friends yet — find people in Discover." :
             tab === "BLOCKED" ? "No blocked users." : "No people match your search."}
          </p>
        ) : (
          list.map((p) => <UserTile key={p.id} person={p} onClick={setSelected} token={token} />)
        )}
      </div>
    </div>
  );
}
